// @ts-strict-ignore
import React, { useCallback, useState } from 'react';
import {
  Box, Button, ButtonSize, ButtonVariant, IconName, TextField,
} from '@usealloy/component-library';
import type { ServiceOption } from '../../types';
import { OutlineContainer, EditViewContainer } from '../styles';
import { ColumnBoxWithGrayLabel } from '../ColumnBoxWithGrayLabel';
import { ColumnBox } from '../../styles';

interface OptionBoxProps {
  input: ServiceOption;
  isDisabled?: boolean;
  onChange: (key: string, newValuesAtIndex?: ServiceOption) => void;
  onEdit?: (key: string) => void;
}

export default function OptionBox({
  input: initialInput,
  isDisabled = false,
  onChange,
  onEdit,
}: OptionBoxProps) {
  const [input, updateInput] = useState<ServiceOption>(initialInput);

  const handleOnCancel = useCallback(() => {
    if (!initialInput.key && !initialInput.value) {
      onChange(initialInput.publicId);
    } else {
      updateInput(initialInput);
    }
    onEdit(null);
  }, [initialInput, onChange, onEdit, updateInput]);

  const handleOnDelete = useCallback(
    () => onChange(initialInput.publicId),
    [initialInput.publicId, onChange],
  );
  const handleOnEdit = useCallback(
    () => onEdit(initialInput.publicId),
    [initialInput.publicId, onEdit],
  );
  const handleOnSave = useCallback(
    () => onChange(initialInput.publicId, input),
    [initialInput.publicId, input, onChange],
  );

  // Display view:
  if (isDisabled) {
    return (
      <OutlineContainer>
        <ColumnBoxWithGrayLabel label="Key" text={input.key} />
        <ColumnBoxWithGrayLabel label="Value" text={input.value} />
        <Box
          justifyContent="flex-end"
          style={{ gap: 'var(--cl-space-0)', width: 'auto' }}
        >
          <Button
            icon={IconName.Edit}
            onClick={handleOnEdit}
            size={ButtonSize.small}
            variant={ButtonVariant.tertiary}
            disabled
          />
          <Button
            icon={IconName.Trash}
            onClick={handleOnDelete}
            size={ButtonSize.small}
            variant={ButtonVariant.tertiary}
            disabled
          />
        </Box>
      </OutlineContainer>
    );
  }

  // Edit view:
  return (
    <EditViewContainer>
      <OutlineContainer>
        <ColumnBoxWithGrayLabel label="Key">
          <TextField
            type="text"
            value={input.key}
            disabled={isDisabled}
            onChange={(e => updateInput({ ...input, key: e.target.value }))}
          />
        </ColumnBoxWithGrayLabel>
        <ColumnBoxWithGrayLabel label="Value">
          <TextField
            type="text"
            value={input.value}
            disabled={isDisabled}
            onChange={(e => updateInput({ ...input, value: e.target.value }))}
          />
        </ColumnBoxWithGrayLabel>
        <ColumnBox style={{ width: '20%' }}>
          <Box style={{ paddingTop: '20px' }}>
            <Button
              data-dd-action-name="service-option-box-save"
              disabled={isDisabled || (!input.key && !input.value)}
              icon={IconName.Approved}
              onClick={handleOnSave}
              variant={ButtonVariant.primary}
            />
            <Button
              data-dd-action-name="service-option-box-cancel"
              icon={IconName.Xmark}
              onClick={handleOnCancel}
              disabled={isDisabled}
              variant={ButtonVariant.secondary}
            />
          </Box>
        </ColumnBox>
      </OutlineContainer>
    </EditViewContainer>
  );
}
